import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import Table from '@cloudscape-design/components/table';
import type { CohortRetention } from '@etiquetachef/types';
import {
  formatCohortMonth,
  formatRetentionPct,
  isCohortImmature,
  retentionColor,
} from './cohortFormatter';

const WINDOWS: { key: keyof CohortRetention['retention']; days: number }[] = [
  { key: 'd1', days: 1 },
  { key: 'd7', days: 7 },
  { key: 'd14', days: 14 },
  { key: 'd30', days: 30 },
  { key: 'd60', days: 60 },
  { key: 'd90', days: 90 },
];

/**
 * Heatmap de retenção por coorte × idade; coorte imatura (`isCohortImmature`)
 * não pinta a célula que ainda não aconteceu.
 */
export function CohortHeatmap({
  cohorts,
}: {
  cohorts: readonly CohortRetention[];
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Table<CohortRetention>
      items={[...cohorts]}
      trackBy="cohortMonth"
      variant="container"
      header={<Header variant="h3" description={t('cohorts.heatmap.hint')}>{t('cohorts.heatmap.title')}</Header>}
      empty={<Box textAlign="center" color="inherit">{t('cohorts.empty')}</Box>}
      columnDefinitions={[
        {
          id: 'cohort',
          header: t('cohorts.heatmap.cohort'),
          cell: (c): React.ReactNode => formatCohortMonth(c.cohortMonth),
        },
        {
          id: 'size',
          header: t('cohorts.heatmap.size'),
          cell: (c): React.ReactNode => c.cohortSize,
        },
        ...WINDOWS.map((w) => ({
          id: w.key,
          header: `D${String(w.days)}`,
          cell: (c: CohortRetention): React.ReactNode => {
            if (isCohortImmature(c.cohortMonth, w.days)) {
              return (
                <Box color="text-status-inactive" textAlign="center">
                  —
                </Box>
              );
            }
            const v = c.retention[w.key];
            const churn = c.cumulativeChurn[w.key];
            return (
              <div
                title={`${t('cohorts.heatmap.retention')}: ${formatRetentionPct(v, 1)} · ${t('cohorts.heatmap.churn')}: ${formatRetentionPct(churn, 1)}`}
                style={{
                  backgroundColor: retentionColor(v),
                  color: '#fff',
                  textAlign: 'center',
                  fontWeight: 600,
                  borderRadius: 4,
                  padding: '2px 6px',
                }}
              >
                {formatRetentionPct(v)}
              </div>
            );
          },
        })),
      ]}
    />
  );
}
